import { MoreHoriz } from '@mui/icons-material'
import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material'
import React from 'react'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import DownloadPrompt from 'components/documents/download-prompt'
import {
  Operations as DocumentOperations,
  operations as documentOperations
} from 'states/dux/document/operations'
import { Payload as DocumentPayload } from 'states/dux/document/types'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { NivosFile } from 'types/nivos-file'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  document: DocumentPayload
  user: UserPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    document: DocumentOperations
    user: UserOperations
  }
}

type AppProps = StateProps & DispatchProps

const DashboardRecentDocumentsFC: React.FC<any> = (props: AppProps) => {
  const { document } = props
  const [loading, setLoading] = React.useState(true)
  const [recentFiles, setRecentFiles] = React.useState<NivosFile[]>([])
  const [downloadFile, setDownloadFile] = React.useState<NivosFile>()
  const navigate = useNavigate()

  const DocumentListItem: React.FC<NivosFile> = props => {
    return (
      <Box
        display={'flex'}
        alignItems={'center'}
        padding={1}
        onClick={() => setDownloadFile(props)}
        sx={{
          ':hover': { paddingLeft: 3 },
          cursor: 'pointer',
          transition: '.40s',
          borderRadius: '4px'
        }}
      >
        <Typography variant={'body2'} width={0.3333}>
          {props.name}
        </Typography>
        <Typography variant={'body2'} width={0.3333} align={'center'}>
          {props.createdBy.fullName}
        </Typography>
        <Typography variant={'body2'} width={0.3333} align={'right'}>
          {props.createdAt.toUTCString()}
        </Typography>
      </Box>
    )
  }

  React.useEffect(() => {
    if (recentFiles.length < 1) {
      setLoading(true)
      void get('/document/file/recent/').then((data: NivosFile[]) => {
        const parsedData = data.map(file => objectifyDateStringFields(file))
        setRecentFiles(parsedData)
        setLoading(false)
      })
    }
  }, [document])

  return (
    <Box width={1} height={1} padding={2}>
      {!loading && recentFiles.length > 0 && (
        <>
          <DownloadPrompt file={downloadFile} setFile={setDownloadFile} />
          <Box display={'flex'} alignItems={'center'}>
            <Typography variant={'h3'} flexGrow={1} align={'center'}>
              {props.layout.language.translations.recentDocuments.toUpperCase()}
            </Typography>
          </Box>
          <Box
            width={1}
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
          >
            {recentFiles.map((file, index) => (
              <Box
                key={index}
                sx={{ cursor: 'pointer' }}
                onClick={() => setDownloadFile(file)}
                width={1}
              >
                <Divider />
                <DocumentListItem {...file} />
              </Box>
            ))}
            <Tooltip title={'See more at Documents page.'} placement={'top'}>
              <IconButton
                size={'small'}
                sx={{ marginTop: 2 }}
                onClick={() => navigate('/documents')}
              >
                <MoreHoriz />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}
      {!loading && recentFiles.length === 0 && (
        <Box display={'flex'} flexDirection={'column'}>
          <Typography variant={'h3'} align={'center'}>
            {props.layout.language.translations.recentDocuments.toUpperCase()}
          </Typography>
          <Typography variant={'h4'} align={'center'}>
            No Data
          </Typography>
        </Box>
      )}
      {loading && (
        <Box
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <Typography variant={'h4'}>Loading...</Typography>
        </Box>
      )}
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    document: state.document,
    user: state.user
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  document: documentOperations,
  user: userOperations
})

const DashboardRecentDocuments = connect(
  mapStateToProps,
  mapDispatchToProps
)(DashboardRecentDocumentsFC)

export default DashboardRecentDocuments
